import type { Category, Confession, ConfessionFeedResponse, ReactionType } from '../types/confession';
import { getInitDataRaw } from './telegram';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  let initData: string | undefined;
  try {
    initData = getInitDataRaw();
  } catch (e) {
    console.warn('Failed to get init data:', e);
    initData = undefined;
  }
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(initData && { 'X-Telegram-Init-Data': initData }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
    cache: 'no-store', // Prevent browser caching
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

export type SortOption = 'new' | 'hot' | 'top';
export type TimePeriod = 'day' | 'week' | 'all';

export async function getConfessions(params: {
  category?: Category | 'all';
  cursor?: string;
  limit?: number;
  daily?: boolean;
  sort?: SortOption;
  period?: TimePeriod;
}): Promise<ConfessionFeedResponse> {
  const searchParams = new URLSearchParams();
  
  if (params.category && params.category !== 'all') {
    searchParams.set('category', params.category);
  }
  if (params.cursor) {
    searchParams.set('cursor', params.cursor);
  }
  if (params.limit) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.daily) {
    searchParams.set('daily', 'true');
  }
  if (params.sort) {
    searchParams.set('sort', params.sort);
  }
  if (params.period && params.period !== 'all') {
    searchParams.set('period', params.period);
  }

  const query = searchParams.toString();
  return apiRequest<ConfessionFeedResponse>(`/confessions${query ? `?${query}` : ''}`);
}

export async function getConfession(id: string): Promise<Confession> {
  return apiRequest<Confession>(`/confessions/${id}`);
}

export async function getRandomConfession(): Promise<Confession> {
  return apiRequest<Confession>('/confessions/random');
}

export async function submitConfession(data: {
  text: string;
  category: Category;
}): Promise<Confession> {
  return apiRequest<Confession>('/confessions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface Comment {
  id: string;
  agentName: string;
  text: string;
  trigger: string;
  framework: string;
  createdAt: string;
  reactions: {
    laugh: number;
    mindblown: number;
    cringe: number;
    skull: number;
  };
  userReactions: ReactionType[];
}

export async function getComments(
  confessionId: string,
  params?: {
    sort?: 'top' | 'new';
    limit?: number;
    offset?: number;
  }
): Promise<{ comments: Comment[]; count: number; totalCount: number; hasMore: boolean }> {
  const searchParams = new URLSearchParams();
  if (params?.sort) searchParams.set('sort', params.sort);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());
  
  const query = searchParams.toString();
  return apiRequest<{ comments: Comment[]; count: number; totalCount: number; hasMore: boolean }>(
    `/confessions/${confessionId}/comments${query ? `?${query}` : ''}`
  );
}

export async function reactToComment(
  commentId: string,
  reaction: ReactionType,
  remove: boolean = false
): Promise<{ success: boolean; reactions: Record<ReactionType, number> }> {
  return apiRequest(`/comments/${commentId}/react`, {
    method: 'POST',
    body: JSON.stringify({ reaction, remove }),
  });
}

export async function reactToConfession(
  confessionId: string,
  reaction: ReactionType,
  remove: boolean = false
): Promise<{ success: boolean; reactions: Record<ReactionType, number> }> {
  return apiRequest(`/confessions/${confessionId}/react`, {
    method: 'POST',
    body: JSON.stringify({ reaction, remove }),
  });
}

export async function getDailyTop(): Promise<Confession[]> {
  const response = await getConfessions({ daily: true, limit: 10 });
  return response.confessions;
}

export async function shareConfessionWithImage(confessionId: string): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/confessions/${confessionId}/share`, {
    method: 'POST',
  });
}

// Cache for prepared message IDs (valid for 5 minutes)
const preparedMessageCache = new Map<string, { pmid: string; expiresAt: number }>();
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export async function prepareShareConfession(confessionId: string): Promise<{ 
  success: boolean; 
  prepared_message_id?: string;
  image_url?: string;
  error?: string;
  details?: string;
  fromCache?: boolean;
}> {
  // Check cache first
  const cached = preparedMessageCache.get(confessionId);
  if (cached && cached.expiresAt > Date.now()) {
    console.log(`[prepareShareConfession] Using CACHED prepared_message_id for ${confessionId}`);
    return {
      success: true,
      prepared_message_id: cached.pmid,
      fromCache: true,
    };
  }
  
  try {
    // Get initData once and log it
    const initData = getInitDataRaw();
    console.log('[prepareShareConfession] initData length:', initData?.length || 0);
    
    const response = await fetch(`${API_BASE}/confessions/${confessionId}/prepare-share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(initData && { 'X-Telegram-Init-Data': initData }),
      },
    });

    const data = await response.json();
    
    // Return the full response including errors
    if (!response.ok) {
      console.error('[prepareShareConfession] Backend error:', data);
      return {
        success: false,
        error: data.error || 'Request failed',
        details: data.details,
      };
    }
    
    // Cache the prepared_message_id
    if (data.prepared_message_id) {
      preparedMessageCache.set(confessionId, {
        pmid: data.prepared_message_id,
        expiresAt: Date.now() + CACHE_DURATION_MS,
      });
      console.log(`[prepareShareConfession] Cached prepared_message_id for ${confessionId}`);
    }
    
    return data;
  } catch (error) {
    console.error('[prepareShareConfession] Network error:', error);
    return {
      success: false,
      error: 'Network error',
      details: String(error),
    };
  }
}

// Clear cache for a specific confession (e.g., after reactions change)
export function clearPreparedMessageCache(confessionId?: string) {
  if (confessionId) {
    preparedMessageCache.delete(confessionId);
  } else {
    preparedMessageCache.clear();
  }
}
